import { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import {
  listAccountDocuments,
  type AccountDocument,
} from "@/lib/brokerAccounts";
import { messageFor } from "@/lib/errors";
import { statusBadgeClass } from "@/lib/listFormat";
import {
  ADVANCE_EVIDENCE_REQUIREMENTS,
  ADVANCE_WITH_UNDERWRITER,
  EVIDENCE_COPY,
  advanceEvidenceStateLabel,
  getRequirementDocumentTypes,
  isEvidenceOpen,
  type AdvanceEvidence,
  type AdvanceEvidenceRequirement,
  type AdvanceEvidenceState,
  type ReadAdvanceRequirements,
  type RequirementDocumentTypes,
} from "@/lib/signalAdvanceEvidence";
import { REQUIREMENT_FALLBACK_SENTENCE } from "@/lib/accountRequirements";
import { AccountDocumentSlot } from "@/components/AccountDocumentSlot";

// BRK-S330 — the Signal Advance application's evidence, on the account page.
//
// Two cards, Bank and Accounting, each satisfied INDEPENDENTLY by its link or
// by uploaded documents (plan rule 13). Each card carries its state chip from
// PMS and "Upload documents instead" listing the types PMS serves for that
// requirement. When PMS says both are satisfied the whole block is one
// sentence — the application is with the underwriter — and nothing is offered.
//
// Since BRK-S407 it is the DOCUMENT ALTERNATIVE inside the Signal Advance
// application section (`SignalAdvanceApplication`), whose client link rows
// hand over and send the links — so the cards offer no link of their own: two
// Copy controls for one url is how a broker ends up sending it twice.
//
// DISCRETION (plan rule 25): no figure, no metric, no score, no reason. The
// states are about what was SUPPLIED, never about what it said.

function chipClass(state: AdvanceEvidenceState | null): string {
  switch (state) {
    case "satisfied":
      return statusBadgeClass("approved");
    case "connected":
      return statusBadgeClass("submitted");
    // `documents_received` is published but never emitted in the MVP (PMS-S722
    // reports a met requirement as `satisfied`), so it reads neutrally.
    default:
      return statusBadgeClass("draft");
  }
}

export interface SignalAdvanceEvidenceProps {
  accountId: string;
  evidence: ReadAdvanceRequirements;
  /** Re-read the account after a document changed, so the chips are PMS's. */
  onChanged?: () => void;
}

export function SignalAdvanceEvidence({
  accountId,
  evidence,
  onChanged,
}: SignalAdvanceEvidenceProps) {
  return (
    <section
      aria-labelledby="advance-evidence-heading"
      data-testid="advance-evidence"
      className="flex w-full min-w-0 flex-col gap-4 border-t border-brk-border pt-4"
    >
      <h3
        id="advance-evidence-heading"
        className="text-sm font-semibold text-brk-text"
      >
        Documents instead of links
      </h3>
      {evidence.satisfied ? (
        <p role="status" className="text-sm text-brk-text">
          {ADVANCE_WITH_UNDERWRITER}.
        </p>
      ) : (
        <Cards
          accountId={accountId}
          evidence={evidence}
          onChanged={onChanged}
        />
      )}
    </section>
  );
}

function Cards({
  accountId,
  evidence,
  onChanged,
}: Required<Pick<SignalAdvanceEvidenceProps, "accountId" | "evidence">> & {
  onChanged?: () => void;
}) {
  const { authedRequest } = useAuth();
  const [documents, setDocuments] = useState<AccountDocument[] | null>(null);
  const [docsError, setDocsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await listAccountDocuments(authedRequest, accountId);
        if (!cancelled) setDocuments(body.documents);
      } catch (err) {
        if (!cancelled) setDocsError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, accountId]);

  return (
    <>
      <p className="text-sm text-brk-dim">
        Each part is complete with its link or with uploaded documents — your
        customer can mix the two.
      </p>
      {docsError && (
        <p role="alert" className="text-sm text-brk-danger">
          {docsError}
        </p>
      )}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {ADVANCE_EVIDENCE_REQUIREMENTS.map((requirement) => (
          <EvidenceCard
            key={requirement}
            accountId={accountId}
            requirement={requirement}
            evidence={evidence[requirement]}
            documents={documents}
            onUploaded={(doc) => {
              setDocuments((prev) => [doc, ...(prev ?? [])]);
              onChanged?.();
            }}
            onRemoved={(id) => {
              setDocuments((prev) => (prev ?? []).filter((d) => d.id !== id));
              onChanged?.();
            }}
          />
        ))}
      </div>
    </>
  );
}

function EvidenceCard({
  accountId,
  requirement,
  evidence,
  documents,
  onUploaded,
  onRemoved,
}: {
  accountId: string;
  requirement: AdvanceEvidenceRequirement;
  evidence: AdvanceEvidence;
  documents: AccountDocument[] | null;
  onUploaded: (doc: AccountDocument) => void;
  onRemoved: (id: string) => void;
}) {
  const { authedRequest } = useAuth();
  const copy = EVIDENCE_COPY[requirement];
  // An unknown state is never actionable. A SATISFIED card still lists its
  // files with Remove: the application is not with the underwriter until BOTH
  // are met, and a wrong upload must be removable until then.
  const readable = evidence.state !== null;
  const [accepted, setAccepted] = useState<RequirementDocumentTypes | null>(
    null,
  );
  const [typesError, setTypesError] = useState<string | null>(null);

  useEffect(() => {
    if (!readable) return;
    let cancelled = false;
    void (async () => {
      try {
        const served = await getRequirementDocumentTypes(
          authedRequest,
          requirement,
        );
        if (!cancelled) setAccepted(served);
      } catch (err) {
        if (!cancelled) setTypesError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, requirement, readable]);

  const headingId = `advance-evidence-${requirement}`;
  const mine =
    accepted && documents
      ? documents.filter((d) => accepted.types.some((t) => t.key === d.type))
      : [];

  return (
    <article
      aria-labelledby={headingId}
      data-testid={`advance-evidence-${requirement}`}
      className="flex min-w-0 flex-col gap-3 rounded-lg border border-brk-border p-3 sm:p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 id={headingId} className="font-medium text-brk-text">
          {copy.title}
        </h3>
        <span
          data-testid="advance-evidence-state"
          className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${chipClass(evidence.state)}`}
        >
          {advanceEvidenceStateLabel(evidence.state)}
        </span>
      </div>

      {!readable ? (
        <p className="text-sm text-brk-text">{REQUIREMENT_FALLBACK_SENTENCE}</p>
      ) : (
        <>
          {!isEvidenceOpen(evidence) && (
            <p className="text-sm text-brk-text">
              {evidence.via === "documents"
                ? "Complete with the documents on file."
                : "Complete with the connected link."}
            </p>
          )}
          <div className="flex flex-col gap-2 border-t border-brk-border pt-3">
            <h4 className="text-sm font-semibold text-brk-text">
              Upload documents instead
            </h4>
            <p className="text-sm text-brk-dim">{copy.alternative}</p>
            {typesError && (
              <p role="alert" className="text-sm text-brk-danger">
                {typesError}
              </p>
            )}
            {!accepted && !typesError && (
              <p role="status" className="text-sm text-brk-dim">
                Loading…
              </p>
            )}
            {accepted && accepted.types.length > 0 && (
              <AccountDocumentSlot
                accountId={accountId}
                inputLabel={`Upload ${copy.title.toLowerCase()} documents`}
                types={accepted.types}
                rules={accepted.rules}
                documents={mine}
                onUploaded={onUploaded}
                onRemoved={onRemoved}
              />
            )}
          </div>
        </>
      )}
    </article>
  );
}
